import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { Mail, Copy, Loader2, Sparkles, Link as LinkIcon, Newspaper, User, CreditCard, LogOut, Info } from 'lucide-react';
import Toggle from 'react-toggle';
import "react-toggle/style.css";
import { useAuth } from '../contexts/AuthContext';

interface Email {
  subject: string;
  body: string;
}

interface FormData {
  targetType: 'business' | 'individual';
  businessType: string;
  service: string;
  tone: string;
  linkedinUrl: string;
}

interface LinkedInProfile {
  fullName: string;
  headline: string;
  company: string;
  about: string;
  recent_posts: string[];
}

interface NewsItem {
  title: string;
  description: string;
  url: string;
  publishedAt: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:12001';

const GeneratorPage: React.FC = () => {
  const { user, userProfile, signOut, refreshProfile } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    targetType: 'business',
    businessType: '',
    service: '',
    tone: '',
    linkedinUrl: ''
  });
  const [emails, setEmails] = useState<Email[]>([]);
  const [loading, setLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [tones, setTones] = useState<string[]>([]);
  const [linkedinProfile, setLinkedinProfile] = useState<LinkedInProfile | null>(null);
  const [companyNews, setCompanyNews] = useState<NewsItem[]>([]);
  const [fetchingProfile, setFetchingProfile] = useState(false);

  // Load dropdown options on component mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [businessTypesRes, servicesRes, tonesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/business-types`),
          axios.get(`${API_BASE_URL}/api/services`),
          axios.get(`${API_BASE_URL}/api/tones`)
        ]);

        setBusinessTypes(businessTypesRes.data);
        setServices(servicesRes.data);
        setTones(tonesRes.data);
      } catch (error) {
        console.error('Error loading options:', error);
        toast.error('Failed to load form options');
      }
    };

    loadOptions();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset LinkedIn profile data when changing target type
    if (name === 'targetType') {
      setLinkedinProfile(null);
      setCompanyNews([]);
    }
  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetType = e.target.checked ? 'individual' : 'business';
    setFormData(prev => ({
      ...prev,
      targetType
    }));

    // Reset LinkedIn profile data when changing target type
    setLinkedinProfile(null);
    setCompanyNews([]);
  };

  const fetchLinkedInProfile = async () => {
    if (!formData.linkedinUrl) {
      toast.error('Please enter a LinkedIn URL');
      return;
    }

    setFetchingProfile(true);

    // Show immediate feedback with encouraging message
    toast.loading('🔍 Researching LinkedIn profile...', {
      duration: Infinity,
      id: 'linkedin-generation'
    });

    try {
      const response = await axios.post(`${API_BASE_URL}/api/linkedin/profile`, {
        linkedinUrl: formData.linkedinUrl,
      }, {
        headers: {
          'Authorization': `Bearer ${getAccessTokenFromStorage()}`
        }
      });

      const profile = response.data.profile;
      setLinkedinProfile(profile);

      // If we have a company name, fetch company news
      if (profile.company) {
        try {
          const newsResponse = await axios.get(`${API_BASE_URL}/api/news/company-news/${encodeURIComponent(profile.company)}`);
          setCompanyNews(newsResponse.data.news);
        } catch (error) {
          console.error('Error fetching company news:', error);
        }
      }

      // Dismiss the final progress toast before showing success
      toast.dismiss('linkedin-generation');

      toast.success('🎉 LinkedIn profile data retrieved successfully! Quality research leads to quality outreach!', {
        duration: 5000,
        id: 'linkedin-success'
      });

    } catch (error) {
      console.error('Error fetching LinkedIn profile:', error);
      toast.dismiss('linkedin-generation');
      toast.error('Failed to retrieve LinkedIn profile data');
    } finally {
      setFetchingProfile(false);
    }
  };

  function getAccessTokenFromStorage(): string {
    const key = Object.keys(localStorage).find(key => key.endsWith('auth-token'));
    if (!key) return '';

    try {
      const stored = localStorage.getItem(key);
      if (!stored) return '';

      const session = JSON.parse(stored);
      return session?.access_token || '';
    } catch (err) {
      console.error('Failed to parse session token:', err);
      return '';
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if user has credits
    if (!userProfile || userProfile.credits <= 0) {
      toast.error('You have no credits remaining. Please buy more credits to continue.');
      return;
    }

    // Validation
    if (!formData.service || !formData.tone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.targetType === 'business' && !formData.businessType) {
      toast.error('Please select a business type');
      return;
    }

    if (formData.targetType === 'individual' && !formData.linkedinUrl) {
      toast.error('Please enter a LinkedIn URL');
      return;
    }

    // If targeting an individual but haven't fetched profile data yet, fetch it first
    if (formData.targetType === 'individual' && !linkedinProfile) {
      toast.error('Please fetch LinkedIn profile data first');
      return;
    }

    setLoading(true);

    // Show immediate feedback with encouraging message
    toast.loading('🎯 Crafting your personalized emails... Great things take time!', {
      duration: 4000,
      id: 'email-generation'
    });

    try {
      // Show progress updates with encouraging messages - each stays for 4 seconds
      setTimeout(() => {
        toast.dismiss('email-generation');
        toast.loading('🔍 Analyzing your inputs and researching the target... Patience is a virtue!', {
          duration: 4000,
          id: 'email-progress-1'
        });
      }, 4000);

      setTimeout(() => {
        toast.dismiss('email-progress-1');
        toast.loading('✨ Personalizing your message with AI magic... Good things come to those who wait!', {
          duration: 4000,
          id: 'email-progress-2'
        });
      }, 8000);

      setTimeout(() => {
        toast.dismiss('email-progress-2');
        toast.loading('🚀 Finalizing your cold emails... Almost there! Your patience will be rewarded!', {
          duration: Infinity,
          id: 'email-progress-3'
        });
      }, 12000);

      const response = await axios.post(`${API_BASE_URL}/api/email/generate`, {
        ...formData,
        targetType: formData.targetType
      }, {
        headers: {
          'Authorization': `Bearer ${getAccessTokenFromStorage()}`
        }
      });

      // Dismiss the final progress toast before showing success
      toast.dismiss('email-progress-3');

      setEmails(response.data.emails);
      toast.success('🎉 Your personalized emails are ready! Quality takes time, and it was worth the wait!', {
        duration: 5000,
        id: 'email-success'
      });

      // Refresh user profile to get updated credit count
      await refreshProfile();

    } catch (error: any) {
      console.error('Error generating emails:', error);

      // Dismiss all progress toasts
      toast.dismiss('email-generation');
      toast.dismiss('email-progress-1');
      toast.dismiss('email-progress-2');
      toast.dismiss('email-progress-3');

      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please buy more credits to continue.');
      } else if (error.response?.status === 401) {
        toast.error('Please log in to generate emails.');
      } else {
        toast.error('Failed to generate emails. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Show credit warning when credits are low
  const showCreditWarning = userProfile && userProfile.credits <= 2;
  const showNoCreditError: boolean = !!userProfile && userProfile.credits <= 0;

  // Enhanced Tooltip component with better text wrapping
  const Tooltip: React.FC<{ children: React.ReactNode; content: string }> = ({ children, content }) => (
    <div className="group relative inline-block">
      {children}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-3 px-4 py-3 text-sm text-gray-600 bg-white border border-gray-300 rounded-xl opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-50 w-80 whitespace-normal text-left shadow-xl backdrop-blur-sm">
        <div className="font-medium text-gray-800 mb-2">💡 Tip:</div>
        <div className="leading-relaxed">{content}</div>
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-5 border-r-5 border-b-5 border-transparent border-b-white"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Toaster position="top-right" />

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="bg-primary-500 p-2 rounded-lg">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">First Ping</h1>
                <p className="text-gray-600 text-xs sm:text-sm mt-0.5 md:mt-1">
                  <span className="font-extrabold text-primary-600">AI</span> Powered Outreach {/* Colored and bolder "AI" */}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Credits Display */}
              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-2 rounded-lg">
                <CreditCard className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">
                  {userProfile?.credits || 0} credits
                </span>
              </div>

              {/* User Menu */}
              <div className="flex items-center space-x-2">
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span className="text-sm">Profile</span>
                </Link>

                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-2 text-gray-600 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="text-sm">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Credit Warning/Error Messages */}
      {showNoCreditError && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-4 mt-4 rounded">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">
                <strong>You've used all your free credits!</strong>
                <Link to="/buy-credits" className="ml-2 text-red-800 underline hover:text-red-900">
                  Buy more credits to continue generating emails.
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      {showCreditWarning && !showNoCreditError && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mx-4 mt-4 rounded">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Low credits warning:</strong> You have {userProfile?.credits} credits remaining.
                <Link to="/buy-credits" className="ml-2 text-yellow-800 underline hover:text-yellow-900">
                  Buy more credits
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Input Form */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Target Type Toggle */}
            <div className="flex justify-center items-center space-x-4 mb-4">
              <span className={`text-sm font-medium ${formData.targetType === 'business' ? 'text-primary-600' : 'text-gray-500'}`}>
                Business
              </span>
              <Toggle
                id="targetType"
                checked={formData.targetType === 'individual'}
                onChange={handleToggleChange}
                icons={false}
                className="custom-toggle"
              />
              <span className={`text-sm font-medium ${formData.targetType === 'individual' ? 'text-primary-600' : 'text-gray-500'}`}>
                Individual (LinkedIn)
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Conditional Fields Based on Target Type */}
              {formData.targetType === 'business' ? (
                /* Business Type or Company Name */
                <div>
                  <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-2">
                    Business Type or Company Name
                    <Tooltip content="Choose from dropdown options OR type popular company names like Tesla, Netflix. For smaller companies, use the dropdown.">
                      <Info className="inline h-4 w-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
                    </Tooltip>
                  </label>
                  <input
                    type="text"
                    id="businessType"
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleInputChange}
                    placeholder="e.g., SaaS Company OR Tesla, Netflix"
                    list="businessTypes"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    required={formData.targetType === 'business'}
                  />
                  <datalist id="businessTypes">
                    {businessTypes.map((type, index) => (
                      <option key={index} value={type} />
                    ))}
                  </datalist>
                </div>
              ) : (
                /* LinkedIn URL */
                <div>
                  <label htmlFor="linkedinUrl" className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn Profile URL
                  </label>
                  <div className="flex">
                    <input
                      type="url"
                      id="linkedinUrl"
                      name="linkedinUrl"
                      value={formData.linkedinUrl}
                      onChange={handleInputChange}
                      placeholder="e.g., https://www.linkedin.com/in/username"
                      className="w-full px-4 py-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      required={formData.targetType === 'individual'}
                    />
                    <button
                      type="button"
                      onClick={fetchLinkedInProfile}
                      disabled={fetchingProfile || !formData.linkedinUrl}
                      className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-4 py-3 rounded-r-lg font-medium flex items-center transition-colors"
                    >
                      {fetchingProfile ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <LinkIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {linkedinProfile && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                      <p className="font-medium text-gray-900">{linkedinProfile.fullName}</p>
                      <p className="text-gray-600">{linkedinProfile.headline}</p>
                      <p className="text-gray-600">{linkedinProfile.about}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Service or Query - Common for both types */}
              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Service or Query
                  <Tooltip content="Describe your service or goal. Examples: Web Development, SEO consulting, Applying for Marketing job, Selling design services, Partnership opportunities.">
                    <Info className="inline h-4 w-4 ml-1 text-gray-400 hover:text-gray-600 cursor-help" />
                  </Tooltip>
                </label>
                <input
                  type="text"
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  placeholder="e.g., Web Development OR Applying for Marketing job"
                  list="services"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  required
                />
                <datalist id="services">
                  {services.map((service, index) => (
                    <option key={index} value={service} />
                  ))}
                </datalist>
              </div>

              {/* Tone - Common for both types */}
              <div>
                <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-2">
                  Tone of Voice
                </label>
                <select
                  id="tone"
                  name="tone"
                  value={formData.tone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  required
                >
                  <option value="">Select tone...</option>
                  {tones.map((tone, index) => (
                    <option key={index} value={tone}>{tone}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Display Company News if available */}
            {companyNews.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-2">
                  <Newspaper className="h-5 w-5 text-primary-600 mr-2" />
                  <h3 className="text-sm font-medium text-gray-900">Recent News</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {companyNews.slice(0, 2).map((news, index) => (
                    <li key={index} className="truncate">• {news.title}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading || (formData.targetType === 'individual' && !linkedinProfile) || showNoCreditError}
                className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-8 py-3 rounded-lg font-medium flex items-center space-x-2 transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5" />
                    <span>Generate Cold Emails (1 credit)</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Generated Emails */}
        {emails.length > 0 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 text-center">Your Generated Cold Emails</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {emails.map((email, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Email {index + 1}</h3>
                    <button
                      onClick={() => copyToClipboard(`Subject: ${email.subject}\n\n${email.body}`)}
                      className="text-gray-500 hover:text-primary-600 transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Subject Line:</h4>
                      <p className="text-gray-900 font-medium bg-gray-50 p-3 rounded-lg">
                        {email.subject}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Email Body:</h4>
                      <div className="text-gray-800 bg-gray-50 p-4 rounded-lg whitespace-pre-line leading-relaxed">
                        {email.body}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {emails.length === 0 && !loading && (
          <div className="text-center py-12">
            <Mail className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Generate Cold Emails?</h3>
            <p className="text-gray-600">Fill out the form above to get 3 personalized cold email variations</p>
            {showNoCreditError && (
              <div className="mt-4">
                <Link
                  to="/buy-credits"
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center space-x-2 transition-colors"
                >
                  <CreditCard className="h-4 w-4" />
                  <span>Buy Credits</span>
                </Link>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default GeneratorPage;